import type { TFunction } from "i18next";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FaExclamationTriangle } from "react-icons/fa";
import { U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U2000FormStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { NS } from "../../../../U3121";
import { useU3121Context } from "../../../../U3121Context";
import type { U3121SubProject, U3121Task, U3121TaskAssignment } from "../../../../U3121Service";

interface U3121SubProjectTaskAssignmentBodyProps {
  formStateHandlers: U2000FormStateHandlers<U3121SubProject>;
  t: TFunction;
  onValidateAfterTaskChange?: (sub: U3121SubProject) => void;
}

function getTaskLabel(task: U3121Task, language: string): string {
  if (language === "fr" && task.taskDescriptionFr?.trim()) return task.taskDescriptionFr.trim();
  if (language === "en" && task.taskDescriptionEn?.trim()) return task.taskDescriptionEn.trim();
  return task.taskDescription?.trim() || task.taskCode?.trim() || task.id;
}

function getSelectedTaskIdsFromSubProject(sub: U3121SubProject | null | undefined): Set<string> {
  const raw = sub?.taskAssignments;
  if (!raw?.length) return new Set();
  const ids = (raw as (string | U3121TaskAssignment)[]).map((item) => (typeof item === "string" ? item : item.id));
  return new Set(ids);
}

function U3121SubProjectTaskAssignmentBody({ formStateHandlers, t, onValidateAfterTaskChange }: U3121SubProjectTaskAssignmentBodyProps) {
  const { i18n } = useTranslation();
  const { initLU } = useU3121Context();
  const tasks = initLU.tasks ?? [];
  const language = i18n.language;
  const isEditMode = formStateHandlers.getFormState() !== U2000FormStateEnumState.READ;

  const sub = formStateHandlers.getData();
  const includeTaskAssignments = Boolean(sub?.includeTaskAssignments);
  const selectedTaskIds = getSelectedTaskIdsFromSubProject(sub);
  const errors = formStateHandlers.getErrors();
  const hasTaskAssignmentError = Boolean(errors["taskAssignments"]);
  const isSubmitted = formStateHandlers.isSubmitted();

  const selectAllRef = useRef<HTMLInputElement | null>(null);
  const taskIds = tasks.map((task) => task.id);
  const catalogIdSet = new Set(taskIds);
  const orphanSelectedIds = [...selectedTaskIds].filter((id) => !catalogIdSet.has(id));
  const allSelected = taskIds.length > 0 && taskIds.every((id) => selectedTaskIds.has(id));
  const someSelected = selectedTaskIds.size > 0 && selectedTaskIds.size < taskIds.length;

  const handleIncludeTaskAssignmentsChange = (checked: boolean) => {
    const data = formStateHandlers.getData();
    if (!data) return;
    formStateHandlers.setData({
      ...data,
      includeTaskAssignments: checked,
      ...(checked ? {} : { taskAssignments: [] }),
    });
    if (!checked) {
      const currentErrors = formStateHandlers.getErrors() ?? {};
      const filtered = Object.fromEntries(Object.entries(currentErrors).filter(([key]) => key !== "taskAssignments"));
      if (Object.keys(filtered).length !== Object.keys(currentErrors).length) {
        formStateHandlers.setErrors(filtered);
      }
    }
  };

  useEffect(() => {
    const el = selectAllRef.current;
    if (!el) return;
    el.indeterminate = someSelected;
  }, [someSelected]);

  const updateSubProjectTaskAssignments = (newIds: Set<string>) => {
    const data = formStateHandlers.getData();
    if (!data) return;
    formStateHandlers.setData({
      ...data,
      taskAssignments: Array.from(newIds).map((id) => ({ id })),
    });
  };

  const runValidation = (nextSub: U3121SubProject) => {
    if (onValidateAfterTaskChange) onValidateAfterTaskChange(nextSub);
  };

  const handleSelectAllChange = () => {
    const clearingAll = allSelected || someSelected;
    const newIds = clearingAll ? new Set<string>() : new Set(taskIds);
    const nextSub: U3121SubProject = {
      ...(formStateHandlers.getData() ?? ({} as U3121SubProject)),
      taskAssignments: Array.from(newIds).map((id) => ({ id })),
    };
    updateSubProjectTaskAssignments(newIds);
    runValidation(nextSub);
  };

  const handleTaskChange = (taskId: string, checked: boolean) => {
    const next = new Set(selectedTaskIds);
    if (checked) next.add(taskId);
    else next.delete(taskId);
    const nextSub: U3121SubProject = {
      ...(formStateHandlers.getData() ?? ({} as U3121SubProject)),
      taskAssignments: Array.from(next).map((id) => ({ id })),
    };
    updateSubProjectTaskAssignments(next);
    runValidation(nextSub);
  };

  const checkboxRow = (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-b border-gray-200 pb-3">
      <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700">
        <input
          type="checkbox"
          checked={includeTaskAssignments}
          onChange={(e) => handleIncludeTaskAssignmentsChange(e.target.checked)}
          disabled={!isEditMode}
          className="h-4 w-4 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500"
          aria-label={t("lblIncludeTaskAssignments", { ns: NS })}
          aria-checked={includeTaskAssignments}
        />
        <span>{t("lblIncludeTaskAssignments", { ns: NS })}</span>
      </label>
    </div>
  );

  if (!includeTaskAssignments) {
    return (
      <>
        {checkboxRow}
        <p className="text-sm text-gray-500">{t("msgTaskAssignmentFromProject", { ns: NS })}</p>
      </>
    );
  }

  if (!isEditMode) {
    const selectedTasks = tasks.filter((task) => selectedTaskIds.has(task.id));
    if (selectedTasks.length === 0 && orphanSelectedIds.length === 0) {
      return (
        <>
          {checkboxRow}
          <p className="text-gray-500">{t("msgNoTasksSelected", { ns: NS })}</p>
        </>
      );
    }
    return (
      <>
        {checkboxRow}
        <div
          className="grid w-full grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6"
          role="table"
          aria-label={t("lblTaskAssignment", { ns: NS })}
        >
          {selectedTasks.map((task) => {
            const linguisticLabel = getTaskLabel(task, language);
            const code = task.taskCode?.trim();
            const displayLabel = code && linguisticLabel !== code ? `${linguisticLabel} (${code})` : linguisticLabel;
            return (
              <label key={task.id} role="cell" className="flex min-w-0 cursor-default items-start gap-3 overflow-hidden rounded-sm px-5 py-2.5 text-sm text-gray-800">
                <input type="checkbox" checked disabled readOnly className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border-gray-300 text-blue-600 opacity-60" aria-label={displayLabel} />
                <span className="min-w-0 wrap-break-word">{displayLabel}</span>
              </label>
            );
          })}
          {orphanSelectedIds.map((id) => (
            <label key={id} role="cell" className="flex min-w-0 cursor-default items-start gap-3 overflow-hidden rounded-sm px-5 py-2.5 text-sm text-gray-500 italic">
              <input type="checkbox" checked disabled readOnly className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border-gray-300 text-blue-600 opacity-60" aria-label={t("msgTaskInactiveAssigned", { ns: NS })} />
              <span className="min-w-0 wrap-break-word">{t("msgTaskInactiveAssigned", { ns: NS })}</span>
            </label>
          ))}
        </div>
      </>
    );
  }

  if (tasks.length === 0) {
    return (
      <>
        {checkboxRow}
        <p className="text-gray-500">{t("msgNoTasks", { ns: NS })}</p>
      </>
    );
  }

  return (
    <>
      {checkboxRow}
      {hasTaskAssignmentError && (isSubmitted || selectedTaskIds.size === 0) && (
        <div className="mb-4 flex items-start gap-1.5 rounded-md border-2 border-red-500 bg-red-50 p-3 animate-fade-in">
          <FaExclamationTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
          <p className="text-sm leading-tight text-red-600" role="alert" aria-live="polite">
            {errors["taskAssignments"]}
          </p>
        </div>
      )}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-b border-gray-200 pb-3">
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700">
          <input
            ref={selectAllRef}
            type="checkbox"
            checked={allSelected}
            onChange={handleSelectAllChange}
            className="h-4 w-4 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500"
            aria-label={t("lblSelectAllTasks", { ns: NS })}
            aria-checked={someSelected ? "mixed" : allSelected}
          />
          <span>{t("lblSelectAllTasks", { ns: NS })}</span>
        </label>
      </div>
      <div className="grid w-full grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6" role="table" aria-label={t("lblTaskAssignment", { ns: NS })}>
        {tasks.map((task) => {
          const linguisticLabel = getTaskLabel(task, language);
          const code = task.taskCode?.trim();
          const displayLabel = code && linguisticLabel !== code ? `${linguisticLabel} (${code})` : linguisticLabel;
          const checked = selectedTaskIds.has(task.id);
          return (
            <label key={task.id} role="cell" className="flex min-w-0 cursor-pointer items-start gap-3 overflow-hidden rounded-sm px-5 py-2.5 text-sm text-gray-800 hover:bg-gray-50">
              <input
                type="checkbox"
                value={task.id}
                checked={checked}
                onChange={(e) => handleTaskChange(task.id, e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500"
                aria-label={displayLabel}
              />
              <span className="min-w-0 wrap-break-word">{displayLabel}</span>
            </label>
          );
        })}
      </div>
    </>
  );
}

export default U3121SubProjectTaskAssignmentBody;
